import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import classnames from 'classnames'

import UnauthorizedPage from '../index'
import Form from '../../../components/Form'
import Field from '../../../components/Form/Field'
import InternalLink from '../../../components/InternalLink'
import FieldLabelWrapper from '../../../features/components/Form/FieldLabelWrapper'

import { ROUTES } from '../../../constants/routes'

import { EMAIL, initialValues, validationSchema } from './fields'

import { resetPassword } from '../../../modules/actions/cognito'
import {
  cognitoLoadingSelector,
  resetPasswordSelector,
  resetPasswordErrorSelector
} from '../../../modules/selectors/cognito'

import useAuthStyles from '../../../styles/common/authPages'
import useDrawerFormStyles from '../../../styles/common/drawerForms'

function ResetPassword() {
  const authClasses = useAuthStyles()
  const drawerFormClasses = useDrawerFormStyles()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [submittedEmail, setSubmittedEmail] = useState('')

  const { nextStep } = useSelector(resetPasswordSelector)

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      const email = values[EMAIL]
      setSubmittedEmail(email)
      dispatch(resetPassword(email))
    }
  })

  const resetPasswordAgain = () => {
    dispatch(resetPassword(submittedEmail, true))
  }

  const isSuccess = !!nextStep?.codeDeliveryDetails

  return (
    <UnauthorizedPage helmetTitle="Reset Password" className={authClasses.authPage}>
      <div className={authClasses.formContainer}>
        {!isSuccess ? (
          <>
            <Form
              formik={formik}
              method="post"
              successSubmit={isSuccess}
              errorSelector={resetPasswordErrorSelector}
              isLoadingSelector={cognitoLoadingSelector}
              submitText={t('Reset password')}
            >
              <section className={drawerFormClasses.section}>
                <h3 className={drawerFormClasses.sectionTitle}>Forgotten your password?</h3>
                <p>
                  No stress, we will send you a link to create a new one. Please enter the email address you registered
                  with.
                </p>
                <div className={authClasses.fieldsContainer}>
                  <FieldLabelWrapper label={'Email'} labelFor="email">
                    <Field formik={formik} id="email" name="email" placeholder={t('Your work email')} />
                  </FieldLabelWrapper>
                </div>
              </section>
            </Form>
            <h3 className={classnames(authClasses.authPageSubtitle, authClasses.authPageFooter)}>
              {t('Already have an account?')} <InternalLink to={ROUTES.login}>Log in</InternalLink>
            </h3>
          </>
        ) : (
          <section className={drawerFormClasses.section}>
            <h3 className={drawerFormClasses.sectionTitle}>Check your inbox</h3>
            <p>
              {t('We’ve sent a secure link to')} <strong>{formik.values.email}</strong>{' '}
              {t('that you can use to log in')}
            </p>
            <p>
              {t('Didn’t receive the email? Check your junk folder or')}{' '}
              <span onClick={resetPasswordAgain} className={authClasses.authPageFakeLink}>
                {t(' send email again')}
              </span>
            </p>
          </section>
        )}
      </div>
    </UnauthorizedPage>
  )
}

export default ResetPassword
